import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../lib/api";
import { trackClick } from "../lib/analytics";
import { Newspaper, MessageSquare, Lightbulb, ExternalLink, RefreshCw, Rss } from "lucide-react";

const KIND_META = {
  news: { Icon: Newspaper, color: "#5C85FF", bg: "rgba(61,107,255,0.10)", border: "rgba(61,107,255,0.30)", label: "News" },
  chatter: { Icon: MessageSquare, color: "#F97316", bg: "rgba(249,115,22,0.10)", border: "rgba(249,115,22,0.30)", label: "Chatter" },
  tip: { Icon: Lightbulb, color: "#34D399", bg: "rgba(52,211,153,0.10)", border: "rgba(52,211,153,0.30)", label: "Tip" },
};

function timeAgo(isoOrRfc) {
  if (!isoOrRfc) return null;
  const t = new Date(isoOrRfc);
  if (isNaN(t.getTime())) return null;
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const TABS = [
  { key: "all", label: "All" },
  { key: "news", label: "News" },
  { key: "chatter", label: "Chatter" },
  { key: "tip", label: "Tips" },
];

export function NewsFeed({ title = "Live outage news & speed tips", testid = "news-feed" }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/news/feed");
      setItems(r.data.items || []);
      setErr(null);
    } catch (e) {
      setErr("Couldn't load the feed");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    return tab === "all" ? items : items.filter((i) => i.kind === tab);
  }, [items, tab]);

  return (
    <section className="glass rounded-2xl p-4 sm:p-6" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(61,107,255,0.12)] border border-[rgba(61,107,255,0.25)]">
            <Rss className="w-4 h-4 text-[var(--cobalt-bright)]" />
          </div>
          <h3 className="font-display text-base sm:text-lg font-semibold truncate">{title}</h3>
        </div>
        <button
          onClick={() => { trackClick("news-feed-refresh"); load(); }}
          className="p-2 rounded-md hover:bg-white/5 transition"
          data-testid="news-feed-refresh"
          aria-label="Refresh"
        >
          <RefreshCw className={`w-4 h-4 text-[var(--text-dim)] ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-md bg-[rgba(15,17,32,0.7)] border border-white/10 mb-4 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { trackClick(`news-feed-tab-${t.key}`); setTab(t.key); }}
            data-testid={`news-feed-tab-${t.key}`}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap transition ${
              tab === t.key ? "bg-[rgba(61,107,255,0.2)] text-white" : "text-[var(--text-dim)] hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Scrollable list */}
      <div
        className="relative max-h-[420px] sm:max-h-[480px] overflow-y-auto pr-1 -mr-1 space-y-2"
        data-testid="news-feed-scroll"
        style={{ scrollbarGutter: "stable" }}
      >
        {err && <div className="text-sm text-rose-300 py-4">{err}</div>}
        {loading && !items ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-16 rounded-lg bg-white/3 animate-pulse" />
          ))
        ) : filtered.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] py-6 text-center">Nothing to show right now.</div>
        ) : (
          filtered.map((it, idx) => <FeedItem key={idx} item={it} idx={idx} />)
        )}
      </div>
    </section>
  );
}

function FeedItem({ item, idx }) {
  const m = KIND_META[item.kind] || KIND_META.news;
  const internalLink = item.kind === "news" && item.slug ? `/news/${item.slug}` : null;
  const externalLink = !internalLink && !!item.link;
  const when = timeAgo(item.published);
  const body = (
    <div className="flex gap-3 p-3 rounded-lg border hover:bg-[rgba(61,107,255,0.06)] transition relative min-w-0"
         style={{ background: m.bg, borderColor: m.border }}>
      <div className="w-8 h-8 shrink-0 rounded-md flex items-center justify-center mt-0.5" style={{ background: `${m.color}18`, border: `1px solid ${m.color}35` }}>
        <m.Icon className="w-4 h-4" style={{ color: m.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: m.color }}>
            {m.label}
          </span>
          {item.source && (
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-muted)] truncate max-w-[120px] sm:max-w-[200px]">
              · {item.source}
            </span>
          )}
          {when && <span className="text-[10px] font-mono text-[var(--text-muted)]">· {when}</span>}
          {externalLink && <ExternalLink className="w-3 h-3 text-[var(--text-muted)] ml-auto shrink-0" />}
        </div>
        <div className="text-sm font-medium leading-snug">{item.title}</div>
        {item.kind === "tip" && item.meta?.body && (
          <div className="text-xs text-[var(--text-dim)] mt-1 leading-relaxed">{item.meta.body}</div>
        )}
        {item.kind === "chatter" && item.meta?.comments != null && (
          <div className="text-[11px] text-[var(--text-muted)] font-mono mt-1">{item.meta.comments} comments</div>
        )}
      </div>
    </div>
  );
  if (internalLink) {
    return (
      <RouterLink
        to={internalLink}
        onClick={() => trackClick(`news-feed-open-internal`, { slug: item.slug })}
        data-testid={`news-item-${idx}`}
        className="block"
      >
        {body}
      </RouterLink>
    );
  }
  if (externalLink) {
    return (
      <a
        href={item.link}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackClick(`news-feed-open-${item.kind}`, { idx })}
        data-testid={`news-item-${idx}`}
        className="block"
      >
        {body}
      </a>
    );
  }
  return <div data-testid={`news-item-${idx}`}>{body}</div>;
}
